import React, { useEffect, useState } from "react";
import { Checkbox, Form, Input } from "antd";
import {
  UserOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  ArrowRightOutlined,
  SafetyCertificateOutlined,
  IdcardOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import {
  hideMessage,
  showAuthLoader,
  userSignIn,
  userSignInSSO,
} from "../appRedux/actions";
import { BaseUrlRace } from "../Api/BaseUrl";
import logorace from "../assets/img/locorace.png";
import "./SignIn.css";

// Dua opsi login: akun SmartRace (username/password) atau SSO Eureka (NIK karyawan)
const LOGIN_MODE_LOCAL = "local";
const LOGIN_MODE_SSO = "sso";
const LOGIN_MODE_STORAGE_KEY = "saved_race_login_mode";

// Vector Illustration for Logistics Operations
const LogisticsIllustration = () => (
  <svg
    className="race-illustration-svg"
    viewBox="0 0 460 210"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Sun and background warm dots */}
    <circle cx="365" cy="46" r="14" fill="#fcd34d" opacity="0.85" />
    <circle cx="160" cy="40" r="1.5" fill="#fcd34d" opacity="0.7" />
    <circle cx="205" cy="55" r="1.5" fill="#fcd34d" opacity="0.7" />
    <circle cx="260" cy="32" r="1.5" fill="#fcd34d" opacity="0.7" />
    <circle cx="320" cy="62" r="1.5" fill="#fcd34d" opacity="0.7" />

    {/* Dashed Route Map Path */}
    <path
      d="M 85 115 C 130 75, 175 70, 235 85 S 330 100, 365 110"
      stroke="#f97316"
      strokeWidth="2"
      strokeDasharray="4 4"
      fill="none"
    />

    {/* Location Pin 1 */}
    <g transform="translate(85, 110)">
      <path
        d="M 0 -18 C -5 -18 -9 -14 -9 -9 C -9 -3 0 0 0 0 C 0 0 9 -3 9 -9 C 9 -14 5 -18 0 -18 Z"
        fill="#f05423"
      />
      <circle cx="0" cy="-9" r="3" fill="#ffffff" />
    </g>

    {/* Location Pin 2 */}
    <g transform="translate(152, 90)">
      <path
        d="M 0 -16 C -4.5 -16 -8 -12.5 -8 -8 C -8 -2.5 0 0 0 0 C 0 0 8 -2.5 8 -8 C 8 -12.5 4.5 -16 0 -16 Z"
        fill="#f05423"
      />
      <circle cx="0" cy="-8" r="2.5" fill="#ffffff" />
    </g>

    {/* Location Pin 3 */}
    <g transform="translate(235, 90)">
      <path
        d="M 0 -16 C -4.5 -16 -8 -12.5 -8 -8 C -8 -2.5 0 0 0 0 C 0 0 8 -2.5 8 -8 C 8 -12.5 4.5 -16 0 -16 Z"
        fill="#f05423"
      />
      <circle cx="0" cy="-8" r="2.5" fill="#ffffff" />
    </g>

    {/* Location Pin 4 */}
    <g transform="translate(365, 105)">
      <path
        d="M 0 -18 C -5 -18 -9 -14 -9 -9 C -9 -3 0 0 0 0 C 0 0 9 -3 9 -9 C 9 -14 5 -18 0 -18 Z"
        fill="#f05423"
      />
      <circle cx="0" cy="-9" r="3" fill="#ffffff" />
    </g>

    {/* Ground Plane Line */}
    <line x1="20" y1="185" x2="440" y2="185" stroke="#f1e5d6" strokeWidth="2.5" />

    {/* Left Character (Courier / Field Personnel) */}
    {/* Head & Hair */}
    <ellipse cx="60" cy="115" rx="7" ry="9" fill="#fcd34d" />
    <path d="M 52 112 Q 60 102 68 112 Q 68 106 60 106 Q 52 106 52 112 Z" fill="#ea580c" />
    {/* Body / Shirt */}
    <path d="M 50 125 L 70 125 L 72 152 L 48 152 Z" fill="#1e3a8a" />
    {/* Arms */}
    <rect x="44" y="127" width="6" height="24" rx="3" fill="#1e3a8a" />
    <rect x="70" y="127" width="6" height="24" rx="3" fill="#1e3a8a" />
    {/* Pants / Legs */}
    <rect x="52" y="152" width="6" height="33" fill="#0f172a" />
    <rect x="62" y="152" width="6" height="33" fill="#0f172a" />
    {/* Shoes */}
    <rect x="49" y="182" width="10" height="4" rx="2" fill="#0f172a" />
    <rect x="61" y="182" width="10" height="4" rx="2" fill="#0f172a" />

    {/* Stack of Packages / Cartons */}
    {/* Box 1 (Bottom Left) */}
    <rect x="80" y="158" width="28" height="27" rx="3" fill="#f59e0b" />
    <rect x="91" y="158" width="6" height="27" fill="#d97706" />
    {/* Box 2 (Bottom Right) */}
    <rect x="105" y="163" width="24" height="22" rx="3" fill="#eab308" />
    <rect x="114" y="163" width="5" height="22" fill="#ca8a04" />
    {/* Box 3 (Top) */}
    <rect x="86" y="142" width="20" height="17" rx="2" fill="#f97316" />
    <rect x="94" y="142" width="4" height="17" fill="#ea580c" />

    {/* Delivery Truck */}
    {/* White Box Container */}
    <rect x="145" y="120" width="85" height="52" rx="5" fill="#ffffff" />
    {/* Orange Accent Stripe */}
    <rect x="145" y="138" width="85" height="8" fill="#f05423" />
    {/* Blue Cab */}
    <path
      d="M 230 135 L 255 135 L 268 152 L 268 172 L 230 172 Z"
      fill="#1e3a8a"
    />
    {/* Truck Windshield / Window */}
    <path d="M 234 140 L 250 140 L 258 152 L 234 152 Z" fill="#93c5fd" />
    {/* Front Bumper / Grill */}
    <rect x="264" y="160" width="6" height="10" rx="1" fill="#475569" />
    {/* Truck Wheels */}
    <circle cx="168" cy="175" r="11" fill="#0f172a" />
    <circle cx="168" cy="175" r="5" fill="#94a3b8" />
    <circle cx="210" cy="175" r="11" fill="#0f172a" />
    <circle cx="210" cy="175" r="5" fill="#94a3b8" />
    <circle cx="254" cy="175" r="11" fill="#0f172a" />
    <circle cx="254" cy="175" r="5" fill="#94a3b8" />

    {/* Warehouse Building */}
    <rect x="310" y="105" width="80" height="80" rx="4" fill="#0f172a" />
    {/* Roof Trim */}
    <rect x="306" y="102" width="88" height="6" rx="2" fill="#f05423" />
    {/* Upper Windows */}
    <rect x="322" y="116" width="10" height="10" rx="2" fill="#fed7aa" />
    <rect x="345" y="116" width="10" height="10" rx="2" fill="#fed7aa" />
    <rect x="368" y="116" width="10" height="10" rx="2" fill="#fed7aa" />
    {/* Loading Dock Doorway */}
    <path d="M 335 185 L 335 145 Q 350 142 365 145 L 365 185 Z" fill="#ea580c" />

    {/* Racks / Shelving Unit next to warehouse */}
    <rect x="260" y="118" width="50" height="67" fill="#fed7aa" rx="2" />
    {/* Metal Frame Posts */}
    <rect x="259" y="116" width="3" height="69" fill="#1e3a8a" />
    <rect x="308" y="116" width="3" height="69" fill="#1e3a8a" />
    {/* Shelves */}
    <rect x="259" y="138" width="52" height="3" fill="#1e3a8a" />
    <rect x="259" y="162" width="52" height="3" fill="#1e3a8a" />
    {/* Stored goods on shelves */}
    <rect x="268" y="125" width="14" height="13" rx="2" fill="#d97706" />
    <rect x="286" y="128" width="12" height="10" rx="2" fill="#f59e0b" />
    <rect x="270" y="148" width="15" height="14" rx="2" fill="#ea580c" />
    <rect x="289" y="146" width="12" height="16" rx="2" fill="#d97706" />
    <rect x="266" y="168" width="16" height="14" rx="2" fill="#f59e0b" />
    <rect x="286" y="170" width="14" height="12" rx="2" fill="#ea580c" />
  </svg>
);


const SignIn = () => {
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [currentTime, setCurrentTime] = useState("");
  const [form] = Form.useForm();

  // Mode login aktif + state SSO
  const [loginMode, setLoginMode] = useState(() => {
    try {
      return localStorage.getItem(LOGIN_MODE_STORAGE_KEY) === LOGIN_MODE_SSO
        ? LOGIN_MODE_SSO
        : LOGIN_MODE_LOCAL;
    } catch (e) {
      return LOGIN_MODE_LOCAL;
    }
  });
  const [ssoConfig, setSsoConfig] = useState(null); // { enabled, provider, label }
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoForm] = Form.useForm();

  const dispatch = useDispatch();
  const { authUser, showMessage } = useSelector(({ auth }) => auth);
  const history = useHistory();

  // Dynamic Live Clock
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      setCurrentTime(`${hours}:${minutes}:${seconds} WIB`);
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, []);

  // Reset root zoom and style fixed full height for signin page
  useEffect(() => {
    document.documentElement.classList.add("race-signin-page");
    document.body.classList.add("race-signin-page");
    return () => {
      document.documentElement.classList.remove("race-signin-page");
      document.body.classList.remove("race-signin-page");
    };
  }, []);

  // Preload saved username from localStorage if exists
  useEffect(() => {
    const savedUser = localStorage.getItem("saved_race_user");
    if (savedUser) {
      form.setFieldsValue({ username: savedUser });
      setRememberMe(true);
    }
  }, [form]);

  useEffect(() => {
    if (showMessage) {
      setTimeout(() => {
        dispatch(hideMessage());
      }, 100);
    }
    if (authUser !== null) {
      history.push("/");
    }
  }, [authUser, showMessage, dispatch, history]);

  const onFinish = (values) => {
    setLoading(true);
    if (rememberMe) {
      localStorage.setItem("saved_race_user", values.username);
    } else {
      localStorage.removeItem("saved_race_user");
    }

    dispatch(showAuthLoader());
    dispatch(
      userSignIn(
        {
          username: values.username,
          password: values.password,
        },
        0 // Default login mode (Race)
      )
    )
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        console.log("Login failed:", error);
        setLoading(false);
      });
  };

  // ===================== SSO Eureka =====================
  // Cek ke backend apakah opsi SSO aktif (SSO_LOGIN_URL + public key terpasang)
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BaseUrlRace}auth/sso-config`)
      .then((res) => {
        if (!cancelled) setSsoConfig(res?.data?.data || { enabled: false });
      })
      .catch(() => {
        if (!cancelled) setSsoConfig({ enabled: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Preload NIK SSO yang tersimpan (abaikan email lama dari versi sebelumnya)
  useEffect(() => {
    const savedSsoUser = localStorage.getItem("saved_race_sso_user");
    if (savedSsoUser && !savedSsoUser.includes("@")) {
      ssoForm.setFieldsValue({ emailOrEmpId: savedSsoUser });
    }
  }, [ssoForm]);

  const switchLoginMode = (mode) => {
    setLoginMode(mode);
    try {
      localStorage.setItem(LOGIN_MODE_STORAGE_KEY, mode);
    } catch (e) {
      // abaikan jika storage tidak tersedia
    }
  };

  const onFinishSso = (values) => {
    if (ssoConfig && !ssoConfig.enabled) {
      Swal.fire({
        icon: "info",
        title: "Login SSO",
        html: `<div style="text-align:left;font-size:14px;color:#475569;line-height:1.6;">
            Login SSO belum diaktifkan di server.<br/>
            Admin IT perlu mengisi <b>SSO_LOGIN_URL</b> dan public key SSO pada konfigurasi backend.
            Sementara ini silakan masuk dengan <b>Akun SmartRace</b>.
          </div>`,
        confirmButtonColor: "#F05423",
        confirmButtonText: "Mengerti",
      });
      return;
    }

    setSsoLoading(true);
    if (rememberMe) {
      localStorage.setItem("saved_race_sso_user", values.emailOrEmpId);
    } else {
      localStorage.removeItem("saved_race_sso_user");
    }

    dispatch(
      userSignInSSO({
        emailOrEmpId: values.emailOrEmpId,
        password: values.password,
      })
    )
      .catch(() => {})
      .finally(() => setSsoLoading(false));
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    Swal.fire({
      icon: "info",
      title: "Reset Password",
      text: "Silakan hubungi tim Admin IT atau Supervisor cabang untuk melakukan reset kata sandi Anda.",
      confirmButtonColor: "#F05423",
      confirmButtonText: "Mengerti",
    });
  };

  const handleContactAdmin = (e) => {
    e.preventDefault();
    Swal.fire({
      icon: "question",
      title: "Bantuan & Kontak Cabang",
      html: `
        <div style="text-align: left; font-size: 14px; color: #475569; line-height: 1.6;">
          <p>Jika Anda mengalami kendala saat login atau membutuhkan akses baru, silakan hubungi:</p>
          <ul style="padding-left: 20px; margin-top: 8px;">
            <li><b>Admin Operasional Cabang</b> setempat</li>
            <li><b>Helpdesk IT RACE</b>: it.support@rajacepat.com</li>
          </ul>
        </div>
      `,
      confirmButtonColor: "#F05423",
      confirmButtonText: "Tutup",
    });
  };

  return (
    <div className="race-signin-wrapper">
      {/* LEFT SECTION: Operations & Logistics Showcase */}
      <div className="race-signin-left">
        {/* Header with RACE Official Logo & Live Clock */}
        <div className="race-left-header">
          <div className="race-brand-logo">
            <img
              src={logorace}
              alt="RACE Raja Cepat"
              className="race-official-logo"
            />
          </div>
          <div className="race-live-clock-badge">
            <span className="race-clock-dot" />
            <span>{currentTime || "09:51:59 WIB"}</span>
          </div>
        </div>

        {/* Hero Section & Illustration */}
        <div className="race-left-body">
          <div className="race-tag-line">— JARINGAN LOGISTICS RACE TERPADU</div>
          <h1 className="race-hero-heading">
            Portal Admin SmartRace
          </h1>

          {/* Logistics Illustration Card */}
          <div className="race-illustration-card">
            <LogisticsIllustration />
          </div>
        </div>

        {/* Footer */}
        <div className="race-left-footer">
          <span>© 2026 RACE Operations Platform</span>
          <span>v4.2.0</span>
        </div>
      </div>

      {/* RIGHT SECTION: Sign In Form */}
      <div className="race-signin-right">
        <div className="race-form-container">
          <div className="race-form-tag">— RACE PORTAL</div>
          <h2 className="race-form-title">Selamat datang kembali</h2>

          {/* Pilihan mode login: Akun SmartRace vs SSO Eureka */}
          <div className="race-login-mode" role="tablist" aria-label="Metode login">
            <button
              type="button"
              role="tab"
              aria-selected={loginMode === LOGIN_MODE_LOCAL}
              className={`race-login-mode-btn ${
                loginMode === LOGIN_MODE_LOCAL ? "active" : ""
              }`}
              onClick={() => switchLoginMode(LOGIN_MODE_LOCAL)}
            >
              <UserOutlined />
              <span>Akun SmartRace</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={loginMode === LOGIN_MODE_SSO}
              className={`race-login-mode-btn ${
                loginMode === LOGIN_MODE_SSO ? "active" : ""
              }`}
              onClick={() => switchLoginMode(LOGIN_MODE_SSO)}
            >
              <ApartmentOutlined />
              <span>{ssoConfig?.label || "SSO Eureka"}</span>
              {ssoConfig && !ssoConfig.enabled && (
                <span className="race-login-mode-badge">nonaktif</span>
              )}
            </button>
          </div>

          {/* OPSI 2: SSO Eureka (NIK karyawan + password SSO) */}
          {loginMode === LOGIN_MODE_SSO && (
          <Form
            form={ssoForm}
            layout="vertical"
            name="race_signin_sso"
            onFinish={onFinishSso}
            requiredMark={false}
          >
            <div className="race-sso-info">
              Masuk memakai <b>NIK karyawan</b> dan password <b>SSO Eureka</b>
              (sama dengan akun portal karyawan). Akun Anda harus sudah
              dipetakan ke SmartRace.
            </div>

            <div className="race-form-group">
              <label className="race-field-label">NIK KARYAWAN</label>
              <Form.Item
                name="emailOrEmpId"
                normalize={(value) => (value || "").trim()}
                rules={[
                  {
                    required: true,
                    message: "Silakan masukkan NIK karyawan Anda!",
                  },
                  {
                    // Login SSO hanya menerima NIK, bukan email
                    validator: (_, value) =>
                      value && value.includes("@")
                        ? Promise.reject(
                            new Error(
                              "Gunakan NIK karyawan (mis. P3101), bukan email.",
                            ),
                          )
                        : Promise.resolve(),
                  },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  className="race-custom-input"
                  prefix={<IdcardOutlined className="race-input-icon" />}
                  placeholder="mis. P3101"
                  autoComplete="username"
                />
              </Form.Item>
            </div>

            <div className="race-form-group">
              <label className="race-field-label">PASSWORD SSO</label>
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "Silakan masukkan password SSO Anda!" },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Input.Password
                  className="race-custom-input"
                  prefix={<LockOutlined className="race-input-icon" />}
                  placeholder="••••••••"
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone twoToneColor="#f05423" /> : <EyeInvisibleOutlined />
                  }
                  autoComplete="current-password"
                />
              </Form.Item>
            </div>

            <div className="race-form-options">
              <Checkbox
                className="race-remember-checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              >
                Ingat saya
              </Checkbox>
              <a
                href="#forgot-password"
                onClick={handleForgotPassword}
                className="race-forgot-link"
              >
                Lupa password SSO?
              </a>
            </div>

            <button
              type="submit"
              disabled={ssoLoading || ssoConfig === null}
              className="race-submit-button"
            >
              {ssoLoading ? (
                <span>Memverifikasi ke SSO...</span>
              ) : ssoConfig === null ? (
                <span>Memuat opsi SSO...</span>
              ) : (
                <>
                  <span>Masuk dengan SSO</span>
                  <ArrowRightOutlined className="race-arrow-icon" />
                </>
              )}
            </button>
          </Form>
          )}

          {/* OPSI 1: Akun SmartRace (username / password) */}
          {loginMode === LOGIN_MODE_LOCAL && (
          <Form
            form={form}
            layout="vertical"
            name="race_signin"
            onFinish={onFinish}
            initialValues={{ remember: true }}
            requiredMark={false}
          >
            {/* Username Input */}
            <div className="race-form-group">
              <label className="race-field-label">USERNAME</label>
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: "Silakan masukkan username Anda!" },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  className="race-custom-input"
                  prefix={<UserOutlined className="race-input-icon" />}
                  placeholder="mis. raceerlangga"
                  autoComplete="username"
                />
              </Form.Item>
            </div>

            {/* Password Input */}
            <div className="race-form-group">
              <label className="race-field-label">PASSWORD</label>
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "Silakan masukkan password Anda!" },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Input.Password
                  className="race-custom-input"
                  prefix={<LockOutlined className="race-input-icon" />}
                  placeholder="••••••••"
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone twoToneColor="#f05423" /> : <EyeInvisibleOutlined />
                  }
                  autoComplete="current-password"
                />
              </Form.Item>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="race-form-options">
              <Checkbox
                className="race-remember-checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              >
                Ingat saya
              </Checkbox>
              <a
                href="#forgot-password"
                onClick={handleForgotPassword}
                className="race-forgot-link"
              >
                Lupa password?
              </a>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="race-submit-button"
            >
              {loading ? (
                <span>Memproses...</span>
              ) : (
                <>
                  <span>Masuk</span>
                  <ArrowRightOutlined className="race-arrow-icon" />
                </>
              )}
            </button>
          </Form>
          )}

          {/* Security & Support Footer (berlaku untuk kedua opsi) */}
          <div className="race-form-footer">
            <div className="race-secure-badge">
              <SafetyCertificateOutlined className="race-secure-icon" />
              <span>Terenkripsi & aman</span>
            </div>
            <a
              href="#contact-admin"
              onClick={handleContactAdmin}
              className="race-admin-contact-link"
            >
              Hubungi admin cabang
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
